import { useEffect, useContext, useState, useMemo, useCallback } from "react";
import axios from "axios";
import HeaderBar from "../../../components/HeaderBar";
import LayoutWrapper from "../../../components/LayoutWrapper";
import getCurrentSkin from "../../../utils/getCurrentSkin";
import { ThemeContext } from "../../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Plus, Search, RefreshCcw, FileText, ChevronLeft, ChevronRight } from "lucide-react";

const BASE_URL = "https://nissi-india.com";

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────
const fmt = (v) => v || "";

// ─────────────────────────────────────────────
// STATUS BADGE
// ─────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const s = (status || "").toLowerCase();
  const cls =
    s === "confirmed"
      ? "bg-green-100 text-green-700 border-green-200"
      : s === "waiting"
      ? "bg-yellow-100 text-yellow-700 border-yellow-200"
      : s === "canceled"
      ? "bg-red-100 text-red-700 border-red-200"
      : s === "closed"
      ? "bg-blue-100 text-blue-700 border-blue-200"
      : "bg-gray-100 text-gray-600 border-gray-200";
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${cls}`}>
      {status || "—"}
    </span>
  );
};

// ─────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────
const GoodsReceiptNote_Listing = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  // ── session / branch ──
  const finYearId  = localStorage.getItem("finYearId") || "";
  const branchId   = localStorage.getItem("branchId") || "";
  const branchType = localStorage.getItem("branch") || "HO";

  const screenBadge = useMemo(() => "INFRA SCREEN : A-22", []);

  // ── styles ──
  const inputBase =
    `w-full px-3 py-2 rounded-xl border outline-none transition text-sm ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-xs font-semibold uppercase tracking-wide ${isDark ? "text-gray-300" : "text-gray-600"}`;

  const cardBase =
    `rounded-2xl border shadow-lg overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-bold shadow-md ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-700 hover:bg-gray-50");

  const thClass = `px-3 py-3 text-left text-xs font-bold uppercase tracking-wide text-white whitespace-nowrap`;
  const tdClass = `px-3 py-2.5 text-sm whitespace-nowrap ${isDark ? "text-gray-200" : "text-gray-800"}`;

  // ─────────────────────────────────────────────
  // FILTER STATE — mirrors ASP filter fields
  // ─────────────────────────────────────────────
  const today     = new Date().toISOString().slice(0, 10);
  const sevenAgo  = new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10);

  const [purType,      setPurType]      = useState(branchType === "HO" ? "H" : "B");
  const [grnNo,        setGrnNo]        = useState("");
  const [fromDate,     setFromDate]     = useState(sevenAgo);
  const [toDate,       setToDate]       = useState(today);
  const [supplierId,   setSupplierId]   = useState("");
  const [storeId,      setStoreId]      = useState("");
  const [projectId,    setProjectId]    = useState("");
  const [status,       setStatus]       = useState("");

  // ── dropdown data ──
  const [suppliers,  setSuppliers]  = useState([]);
  const [stores,     setStores]     = useState([]);
  const [projects,   setProjects]   = useState([]);

  // ── grid ──
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(false);

  // ── pagination ──
  const PAGE_SIZE = 10;
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pagedRows  = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // ── GRN No autocomplete ──
  const [grnSuggestions, setGrnSuggestions] = useState([]);
  const [showSuggest,    setShowSuggest]    = useState(false);

  // ─────────────────────────────────────────────
  // LOAD DROPDOWNS ON MOUNT
  // ─────────────────────────────────────────────
  useEffect(() => {
    loadDropdowns();
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // reload suppliers when purType changes (mirrors rbtn_ho/rbtn_brch_CheckedChanged)
  useEffect(() => {
    loadSuppliers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purType]);

  const loadDropdowns = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/grn-listing/dropdowns`, {
        params: { branchId, finYearId, purType },
      });
      if (res?.data?.success) {
        setSuppliers(res.data.suppliers || []);
        setStores(res.data.stores || []);
        setProjects(res.data.projectNames || []);
      }
    } catch (err) {
      console.error("dropdown load error:", err.message);
    }
  };

  const loadSuppliers = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/grn-listing/suppliers-by-type`, {
        params: { branchId, finYearId, purType },
      });
      if (res?.data?.success) setSuppliers(res.data.suppliers || []);
    } catch { /* silent */ }
  };

  // ─────────────────────────────────────────────
  // GRN NO AUTOCOMPLETE — mirrors GetCompletionList
  // ─────────────────────────────────────────────
  const handleGrnNoChange = async (val) => {
    setGrnNo(val);
    if (val.length < 1) { setGrnSuggestions([]); setShowSuggest(false); return; }
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/grn-listing/autocomplete`, {
        params: { prefix: val, branchId, purType, supplierId: supplierId || 0 },
      });
      if (res?.data?.success) {
        setGrnSuggestions(res.data.items || []);
        setShowSuggest(true);
      }
    } catch { /* silent */ }
  };

  // ─────────────────────────────────────────────
  // DATE VALIDATION — mirrors Check_from_to()
  // ─────────────────────────────────────────────
  const validateDates = () => {
    if (fromDate && fromDate > today) {
      toast.warn("Select from date lesser than current date");
      return false;
    }
    if (toDate && toDate > today) {
      toast.warn("Select to date lesser than current date");
      return false;
    }
    if (fromDate && toDate && toDate < fromDate) {
      toast.warn("Please select from date less than to date");
      return false;
    }
    return true;
  };

  // ─────────────────────────────────────────────
  // SEARCH — mirrors ImgBtn_search_Click → BindGRNGrid → usp_GetBindGridinGRNListing
  // ─────────────────────────────────────────────
  const handleSearch = useCallback(async () => {
    if (!validateDates()) return;
    try {
      setLoading(true);
      setPage(1);
      const res = await axios.get(`${BASE_URL}/api/inventory/grn-listing/list`, {
        params: {
          branchId,
          finYearId,
          purType,
          supplierId:  supplierId  || "",
          grnNo:       grnNo       || "",
          fromDate:    fromDate    || "",
          toDate:      toDate      || "",
          status:      status      || "",
          storeId:     storeId     || "",
          projectId:   projectId   || "",
        },
      });

      console.log("record", res.data);
      
      if (res?.data?.success) {
        setRows(res.data.items || []);
      } else {
        toast.error(res?.data?.message || "Search failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Search error");
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, finYearId, purType, supplierId, grnNo, fromDate, toDate, status, storeId, projectId]);

  // ─────────────────────────────────────────────
  // VIEW GRN (EDIT) — mirrors gv_grnlist_RowCommand VIEW_GRN
  // Gets POs for that GRN, sets sessionStorage payload, navigates to entry page
  // Row is disabled if status is Closed or Confirmed (mirrors gv_grnlist_RowDataBound)
  // ─────────────────────────────────────────────
  const handleViewGrn = async (row) => {
  const s = (row.status || "").toLowerCase();
  if (s === "closed" || s === "confirmed") return;

  try {
    const res = await axios.get(`${BASE_URL}/api/inventory/grn-listing/purchase-orders`, {
      params: { grnId: row.fld_grnid },
    });
    if (res?.data?.success) {
      const payload = {
        grnId:      row.fld_grnid,
        supplierId: row.fld_supplierid,
        poIds:      (res.data.purchaseOrders || []).map((p) => p.fld_purchaseorderid),
      };
      sessionStorage.setItem("grnSelectionPayload", JSON.stringify(payload));
      window.location.href = "/Inventory/GoodReceiptNote_EntryGST";
    } else {
      toast.error(res?.data?.message || "Failed to load GRN data");
    }
  } catch (err) {
    toast.error(err?.response?.data?.message || err.message || "Error loading GRN");
  }
};

  // ─────────────────────────────────────────────
  // ADD NEW — mirrors ImgBtn_AddNew_Click
  // Clears payload, navigates to add listing
  // ─────────────────────────────────────────────
  const handleAddNew = () => {
    sessionStorage.removeItem("grnSelectionPayload");
    window.location.href = "/Inventory/GoodsReceiptNote_Add";
  };

  // ─────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────
  return (
    <LayoutWrapper>
      <HeaderBar skin={skin} setSkin={setSkin} />
      <ToastContainer position="top-right" autoClose={3000} theme={isDark ? "dark" : "light"} />

      <div className={`min-h-screen p-4 md:p-6 ${isDark ? "bg-gray-900/50" : "bg-gray-50"}`}>
        {/* ── PAGE HEADER ── */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <h1 className={`text-2xl font-bold tracking-tight ${isDark ? "text-white" : "text-gray-900"}`}>
              GRN Listing
            </h1>
            <p className={`text-xs mt-0.5 ${isDark ? "text-gray-400" : "text-gray-500"}`}>{screenBadge}</p>
          </div>
          <button onClick={handleAddNew} className={primaryBtn}>
            <Plus size={16} /> Add New
          </button>
        </div>

        {/* ── FILTER CARD ── */}
        <div className={`${cardBase} mb-6`}>
          <div className={`px-5 py-3 border-b ${isDark ? "border-white/10 bg-white/5" : "border-gray-100 bg-gray-50"}`}>
            <div className="flex items-center gap-3">
              <Search size={15} className={isDark ? "text-gray-400" : "text-gray-500"} />
              <span className={`text-sm font-semibold ${isDark ? "text-white" : "text-gray-800"}`}>Search Filters</span>

              {/* HO / Branch radio — mirrors rbtn_ho / rbtn_brch */}
              <div className="flex items-center gap-4 ml-4">
                {[{ label: "H.O", value: "H" }, { label: "Branch", value: "B" }].map((opt) => (
                  <label key={opt.value} className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="radio"
                      name="purType"
                      value={opt.value}
                      checked={purType === opt.value}
                      onChange={() => setPurType(opt.value)}
                      className="accent-blue-500"
                    />
                    <span className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* GRN No with autocomplete */}
            <div className="relative">
              <label className={`${labelBase} block mb-1`}>GRN No</label>
              <input
                type="text"
                className={inputBase}
                placeholder="Search GRN No..."
                value={grnNo}
                onChange={(e) => handleGrnNoChange(e.target.value)}
                onBlur={() => setTimeout(() => setShowSuggest(false), 200)}
              />
              {showSuggest && grnSuggestions.length > 0 && (
                <div className={`absolute z-50 w-full mt-1 rounded-xl border shadow-xl overflow-hidden ${isDark ? "bg-gray-800 border-white/10" : "bg-white border-gray-200"}`}>
                  {grnSuggestions.map((s, i) => (
                    <div
                      key={i}
                      className={`px-3 py-2 text-sm cursor-pointer ${isDark ? "hover:bg-white/10 text-gray-200" : "hover:bg-blue-50 text-gray-800"}`}
                      onMouseDown={() => { setGrnNo(s); setShowSuggest(false); }}
                    >
                      {s}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Store */}
            <div>
              <label className={`${labelBase} block mb-1`}>Store</label>
              <select value={storeId} onChange={(e) => setStoreId(e.target.value)}
                className={inputBase}>
                <option value="">Select</option>
                {stores.map((s) => (
                  <option key={s.storeId} value={s.storeId}>{s.storeName}</option>
                ))}
              </select>
            </div>

            {/* From Date */}
            <div>
              <label className={`${labelBase} block mb-1`}>From Date</label>
              <input type="date" className={inputBase} value={fromDate}
                onChange={(e) => setFromDate(e.target.value)} max={today} />
            </div>

            {/* To Date */}
            <div>
              <label className={`${labelBase} block mb-1`}>To Date</label>
              <input type="date" className={inputBase} value={toDate}
                onChange={(e) => setToDate(e.target.value)} max={today} />
            </div>

            {/* Project Name */}
            <div>
              <label className={`${labelBase} block mb-1`}>Project Name</label>
              <select value={projectId} onChange={(e) => setProjectId(e.target.value)}
                className={inputBase}>
                <option value="">Select</option>
                {projects.map((p) => (
                  <option key={p.projectId} value={p.projectId}>{p.projectName}</option>
                ))}
              </select>
            </div>

            {/* Status */}
            <div>
              <label className={`${labelBase} block mb-1`}>Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)}
                className={inputBase}>
                <option value="">Select</option>
                <option value="Waiting">Waiting</option>
                <option value="Canceled">Canceled</option>
              </select>
            </div>

            {/* Supplier */}
            <div>
              <label className={`${labelBase} block mb-1`}>Supplier</label>
              <select value={supplierId} onChange={(e) => setSupplierId(e.target.value)}
                className={inputBase}>
                <option value="">Select</option>
                {suppliers.map((s) => (
                  <option key={s.supplierId} value={s.supplierId}>{s.supplierName}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Search button */}
          <div className={`px-5 py-3 border-t flex justify-center ${isDark ? "border-white/10" : "border-gray-100"}`}>
            <button onClick={handleSearch} disabled={loading} className={primaryBtn}>
              {loading ? <RefreshCcw size={15} className="animate-spin" /> : <Search size={15} />}
              {loading ? "Searching..." : "Search"}
            </button>
          </div>
        </div>

        {/* ── GRID CARD ── */}
        <div className={cardBase}>
          <div className={`px-5 py-3 border-b flex items-center justify-between ${isDark ? "border-white/10 bg-white/5" : "border-gray-100 bg-gray-50"}`}>
            <div className="flex items-center gap-2">
              <FileText size={15} className={isDark ? "text-gray-400" : "text-gray-500"} />
              <span className={`text-sm font-semibold ${isDark ? "text-white" : "text-gray-800"}`}>
                GRN Records
              </span>
              <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-bold ${isDark ? "bg-white/10 text-gray-300" : "bg-gray-100 text-gray-600"}`}>
                {rows.length}
              </span>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={`bg-${baseColor}-${baseShade}`}>
                  <th className={thClass}>Sl No</th>
                  <th className={thClass}>GRN No.</th>
                  <th className={thClass}>Date</th>
                  <th className={thClass}>Supplier</th>
                  <th className={thClass}>Invoice No</th>
                  <th className={thClass}>Invoice Date</th>
                  <th className={thClass}>Status</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="text-center py-12">
                      <RefreshCcw size={24} className="animate-spin mx-auto text-blue-500 mb-2" />
                      <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>Loading...</p>
                    </td>
                  </tr>
                ) : pagedRows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-12">
                      <FileText size={32} className={`mx-auto mb-2 ${isDark ? "text-gray-600" : "text-gray-300"}`} />
                      <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>No Records Found</p>
                    </td>
                  </tr>
                ) : (
                  pagedRows.map((row, idx) => {
                    const s = (row.status || "").toLowerCase();
                    const isDisabled = s === "closed" || s === "confirmed";
                    const rowNum = (page - 1) * PAGE_SIZE + idx + 1;
                    return (
                      <tr
                        key={row.fld_grnid}
                        className={`border-b transition-colors ${
                          isDark ? "border-white/5 hover:bg-white/5" : "border-gray-100 hover:bg-blue-50/40"
                        } ${idx % 2 === 1 ? (isDark ? "bg-white/3" : "bg-gray-50/60") : ""}`}
                      >
                        <td className={`${tdClass} text-center`}>{rowNum}</td>
                        <td className={tdClass}>
                          {/* mirrors ButtonField VIEW_GRN — disabled if Closed/Confirmed */}
                          <button
                            onClick={() => handleViewGrn(row)}
                            disabled={isDisabled}
                            className={`font-semibold text-sm transition-colors ${
                              isDisabled
                                ? "text-gray-400 cursor-not-allowed"
                                : "text-blue-500 hover:text-blue-700 hover:underline cursor-pointer"
                            }`}
                          >
                            {fmt(row.fld_grnno || row.FLD_grnNo)}
                          </button>
                        </td>
                        <td className={tdClass}>{fmt(row.fld_date)}</td>
                        <td className={tdClass}>{fmt(row.fld_suppliername)}</td>
                        <td className={tdClass}>{fmt(row.fld_Invno || row.fld_invno)}</td>
                        <td className={`${tdClass} text-center`}>{fmt(row.fld_InvDate || row.fld_invdate)}</td>
                        <td className={`${tdClass} text-center`}>
                          <StatusBadge status={row.status} />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* ── PAGINATION ── */}
          {rows.length > PAGE_SIZE && (
            <div className={`px-5 py-3 border-t flex items-center justify-between ${isDark ? "border-white/10" : "border-gray-100"}`}>
              <p className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                Page {page} of {totalPages} ({rows.length} records)
              </p>
              <div className="flex items-center gap-2">
                <button onClick={() => setPage(1)} disabled={page === 1} className={ghostBtn}>
                  First
                </button>
                <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className={ghostBtn}>
                  <ChevronLeft size={14} />
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const start = Math.max(1, Math.min(page - 2, totalPages - 4));
                  const pg = start + i;
                  return pg <= totalPages ? (
                    <button
                      key={pg}
                      onClick={() => setPage(pg)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-all ${
                        pg === page
                          ? `${primaryBtnClass} text-white`
                          : isDark ? "text-gray-300 hover:bg-white/10" : "text-gray-600 hover:bg-gray-100"
                      }`}
                    >
                      {pg}
                    </button>
                  ) : null;
                })}
                <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className={ghostBtn}>
                  <ChevronRight size={14} />
                </button>
                <button onClick={() => setPage(totalPages)} disabled={page === totalPages} className={ghostBtn}>
                  Last
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </LayoutWrapper>
  );
};

export default GoodsReceiptNote_Listing;